'use client';

import { useTranslations } from 'next-intl';
import type { NewsletterSubscriptionResolved } from './types';
import { useNewsletterSubmit } from './useNewsletterSubmit';
import { Button } from '@/components/Button';
import { useAppForm } from '@/lib/form/hook';
import { createFormSchemas } from '@/lib/form/schemas';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';

// Fallback for renders without a `privacyPolicyUrl` in the render context —
// the URL the consent text in 10.03.03.25 spells out (SH-368 D9).
const FALLBACK_PRIVACY_POLICY_URL =
  'https://www.schuelerhilfe.de/ueber-diese-seite/datenschutz/';

const NEWSLETTER_FORM_DEFAULTS = { email: '', consent: false };

/*
 * The form measure (70.14-Widget-Width § 5): narrower than the page on
 * purpose, because a form is read and filled rather than scanned — see
 * 10.03.04.01, which treats the form container width as its own property.
 * Only the measure; the gutter comes from the SlotRenderer wrapper.
 */
const containerClassName = 'mx-auto max-w-4xl pt-4';

export function NewsletterSubscriptionComponent({
  resolved,
}: {
  resolved: NewsletterSubscriptionResolved;
}) {
  const t = useTranslations('newsletter');
  const tValidation = useTranslations('validation');
  const schemas = createFormSchemas(tValidation);

  // The widget is placeable on pages that have no render context at all
  // (e.g. the Strapi preview iframe), so the optional hook is the right one.
  const privacyPolicyUrl =
    useOptionalRenderContext()?.entities.privacyPolicyUrl ??
    FALLBACK_PRIVACY_POLICY_URL;

  const { submitted, hasError, handleSubmit } = useNewsletterSubmit();

  const form = useAppForm({
    defaultValues: NEWSLETTER_FORM_DEFAULTS,
    onSubmit: async ({ value }) => {
      await handleSubmit(schemas.emailSchema.parse(value.email));
    },
  });

  if (submitted) {
    // Mirrors the legacy site's `alert-success` panel: pale green block, dark
    // green text, left-aligned, no icon, 16px radius. Deliberately not
    // `SubmittedCard` — that is the centered checkmark treatment the contact
    // forms use, and this confirmation looks nothing like it.
    return (
      <div className={containerClassName}>
        <div
          role="status"
          className="bg-green-light text-green-dark rounded-section p-4 text-sm/6"
        >
          {resolved.confirmationBody?.length ? (
            // Editor-authored copy (SH-368 follow-up). The base layer gives
            // every `p` a 16px bottom margin, which is the gap the default
            // copy uses between its two paragraphs; the last one is reset so
            // the panel keeps its 16px padding at the bottom.
            <div className="[&_p:last-child]:mb-0">
              <RichText nodes={resolved.confirmationBody} />
            </div>
          ) : (
            <>
              <p>
                {t('successThanks')}
                <br />
                {t('successPending')}
              </p>
              <p className="mt-4 mb-0!">
                {t('successRegards')}
                <br />
                {t('successTeam')}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        noValidate
      >
        {/* Mobile stacks input → consent → button; desktop stretches the
            input and the consent text to the container width and keeps the
            button below (10.03.03.25 AC 1). */}
        <div className="flex flex-col gap-4">
          <form.AppField
            name="email"
            validators={{
              onBlur: schemas.emailSchema,
              onSubmit: schemas.emailSchema,
            }}
          >
            {(field) => (
              <field.TextField
                label={t('emailLabel')}
                type="email"
                autoComplete="email"
                required
              />
            )}
          </form.AppField>

          <form.AppField
            name="consent"
            validators={{ onSubmit: schemas.consentSchema }}
          >
            {(field) => (
              <field.Checkbox>
                {resolved.consentText?.length ? (
                  // Editor-authored copy (SH-368 follow-up). Paragraph margins
                  // are dropped so a single-paragraph text sits flush with the
                  // box, and links get the same treatment as the default text's
                  // `<privacyLink>` — the editor supplies the href themselves,
                  // so this variant does not use the domain's privacyPolicyUrl.
                  <div className="[&_a]:text-primary [&_a]:underline [&_p]:mb-0">
                    <RichText nodes={resolved.consentText} />
                  </div>
                ) : (
                  t.rich('consentLabel', {
                    privacyLink: (chunks) => (
                      <a
                        href={privacyPolicyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline"
                      >
                        {chunks}
                      </a>
                    ),
                  })
                )}
              </field.Checkbox>
            )}
          </form.AppField>

          {hasError && (
            <p className="text-red-600" role="alert">
              {t('submissionError')}
            </p>
          )}

          <form.Subscribe
            selector={(s) => [s.canSubmit, s.isSubmitting] as const}
          >
            {([canSubmit, isSubmitting]) => (
              <Button
                size="lg"
                type="submit"
                disabled={!canSubmit || isSubmitting}
                // `mt-2` adds to the column's `gap-4`, so the button sits
                // 24px below the consent row rather than the 16px used
                // between the other rows.
                //
                // No `w-full`: the button keeps its intrinsic width on mobile
                // (`Button` is inline-flex with its own padding) and `mx-auto`
                // centres it as a flex item at every breakpoint.
                className="mx-auto mt-2 text-sm md:w-sm"
              >
                {isSubmitting ? '…' : t('submitButton')}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  );
}
